import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Info, ArrowUpRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutPremium = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [includedCategory, setIncludedCategory] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const { trackStartCheckout } = useAnalytics();

  const basePrice = 400;

  // Categories for Premium (required field)
  const categories = [
    { id: 'sito', nameKey: 'checkout.premium.categories.sito' },
    { id: 'social', nameKey: 'checkout.premium.categories.social' },
    { id: 'ads', nameKey: 'checkout.premium.categories.ads' },
    { id: 'email', nameKey: 'checkout.premium.categories.email' },
    { id: 'seo', nameKey: 'checkout.premium.categories.seo' }
  ];

  const socialPlatforms = ['Instagram', 'Facebook', 'LinkedIn', 'TikTok'];
  const adsPlatforms = ['Google Ads', 'Meta Ads', 'LinkedIn Ads', 'TikTok Ads'];

  // Available add-ons for Premium (code-based)
  const availableAddons = [
    {
      code: 'addon_social_extra_monthly',
      nameKey: 'checkout.addons.social_extra.name',
      descKey: 'checkout.addons.social_extra.description',
      priceMonthly: 400,
      showIf: () => includedCategory === 'social'
    },
    {
      code: 'addon_ads_extra_monthly',
      nameKey: 'checkout.addons.ads_extra.name',
      descKey: 'checkout.addons.ads_extra.description',
      priceMonthly: 400,
      showIf: () => includedCategory === 'ads'
    },
    {
      code: 'addon_seo_monthly',
      nameKey: 'checkout.addons.seo.name',
      descKey: 'checkout.addons.seo.description',
      priceMonthly: 500,
      showIf: () => includedCategory !== 'seo'
    },
    {
      code: 'addon_gmb_monthly',
      nameKey: 'checkout.addons.gmb.name',
      descKey: 'checkout.addons.gmb.description',
      priceMonthly: 100
    },
    {
      code: 'oneshot_website',
      nameKey: 'checkout.addons.website.name',
      descKey: 'checkout.addons.website.description',
      priceOneShot: 800
    },
    {
      code: 'oneshot_logo',
      nameKey: 'checkout.addons.logo.name',
      descKey: 'checkout.addons.logo.description',
      priceOneShot: 650
    },
    {
      code: 'oneshot_gmb_setup',
      nameKey: 'checkout.addons.gmb_setup.name',
      descKey: 'checkout.addons.gmb_setup.description',
      priceOneShot: 200
    }
  ];

  const toggleAddon = (addonCode) => {
    setSelectedAddons(prev =>
      prev.includes(addonCode)
        ? prev.filter(c => c !== addonCode)
        : [...prev, addonCode]
    );
  };

  const handleCategoryChange = (categoryId) => {
    setIncludedCategory(categoryId);
    setSelectedPlatform('');
    // Remove category-specific addons when category changes
    setSelectedAddons(prev => prev.filter(code => 
      !['addon_social_extra_monthly', 'addon_ads_extra_monthly'].includes(code)
    ));
  };

  const filteredAddons = useMemo(() => {
    return availableAddons.filter(addon => {
      if (addon.showIf) return addon.showIf();
      return true;
    });
  }, [includedCategory]);

  const totals = useMemo(() => {
    let monthly = basePrice;
    let oneShot = 0;

    selectedAddons.forEach(code => {
      const addon = availableAddons.find(a => a.code === code);
      if (addon) {
        if (addon.priceMonthly) monthly += addon.priceMonthly;
        if (addon.priceOneShot) oneShot += addon.priceOneShot;
      }
    });

    return { monthly, oneShot };
  }, [selectedAddons]);

  const showUpgradeMessage = useMemo(() => {
    const monthlyAddons = selectedAddons.filter(code => {
      const addon = availableAddons.find(a => a.code === code);
      return addon && addon.priceMonthly;
    });
    return monthlyAddons.length >= 2;
  }, [selectedAddons]);

  const handleCheckout = async () => {
    if (!customerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      toast.error(t('checkout.email_error'));
      return;
    }

    if (!includedCategory) {
      toast.error(t('checkout.select_category'));
      return;
    }

    if ((includedCategory === 'social' || includedCategory === 'ads') && !selectedPlatform) {
      toast.error(t('checkout.select_platform'));
      return;
    }

    // Track checkout start
    trackStartCheckout('premium', totals.monthly, totals.oneShot, selectedAddons);

    setIsProcessing(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package: 'premium',
          selectedAddons: selectedAddons,
          includedCategory: includedCategory,
          selectedPlatform: selectedPlatform || null,
          customerEmail: customerEmail
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('common.error'));
      }

      window.location.href = data.checkoutUrl;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.message || t('common.error'));
      setIsProcessing(false);
    }
  };

  return (
    <main className="pt-20 min-h-screen bg-[#161716]">
      {/* Header */}
      <section className="py-16 bg-[#161716] border-b border-[#343633]">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <Link to="/servizi" className="text-[#9a9a96] hover:text-[#c8f000] transition-colors text-sm mb-6 inline-flex items-center gap-2">
            {t('checkout.back_to_packages')}
          </Link>
          <div className="flex items-start justify-between flex-wrap gap-6">
            <div>
              <span className="text-[#6f716d] text-sm uppercase tracking-wider mb-2 block">Checkout</span>
              <h1 className="text-[#c8f000] font-bold text-4xl md:text-5xl mb-2">{t('checkout.premium.title')}</h1>
              <p className="text-[#9a9a96] text-lg">{t('checkout.premium.subtitle')}</p>
            </div>
            <div className="text-right">
              <span className="text-4xl font-bold text-white">CHF {basePrice}</span>
              <span className="text-[#9a9a96] ml-2">{t('checkout.per_month')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Step 1: Categoria operativa inclusa (OBBLIGATORIA) */}
      <section className="py-12 bg-[#1f211f]">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-full bg-[#c8f000] flex items-center justify-center text-[#161716] font-bold text-sm">
              1
            </div>
            <h2 className="text-white font-bold text-xl">{t('checkout.premium.step1_title')} <span className="text-[#c8f000]">*</span></h2>
          </div>
          <p className="text-[#9a9a96] mb-8 ml-11">
            {t('checkout.premium.step1_desc')}
          </p>

          <div className="space-y-3 ml-11">
            {categories.map((category) => (
              <div
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`bg-[#2a2c29] p-5 rounded-xl border cursor-pointer transition-all flex items-center gap-4 ${
                  includedCategory === category.id
                    ? 'border-[#c8f000]'
                    : 'border-[#343633] hover:border-[#6f716d]'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  includedCategory === category.id ? 'border-[#c8f000]' : 'border-[#6f716d]'
                }`}>
                  {includedCategory === category.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-[#c8f000]"></div>
                  )}
                </div>
                <span className="text-white font-medium">{t(category.nameKey)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 2: Selezione piattaforma (condizionale) */}
      {(includedCategory === 'social' || includedCategory === 'ads') && (
        <section className="py-12 bg-[#161716]">
          <div className="max-w-[900px] mx-auto px-5 md:px-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#c8f000] flex items-center justify-center text-[#161716] font-bold text-sm">
                2
              </div>
              <h2 className="text-white font-bold text-xl">
                {includedCategory === 'social'
                  ? t('checkout.premium.step2_social')
                  : t('checkout.premium.step2_ads')
                }
              </h2>
            </div>

            {includedCategory === 'ads' && (
              <div className="bg-[#2a2c29] p-4 rounded-lg border border-[#343633] mb-6 ml-11">
                <p className="text-[#9a9a96] text-sm">
                  <Info className="inline-block text-[#c8f000] mr-2" size={16} />
                  {t('checkout.budget_note')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 ml-11">
              {(includedCategory === 'social' ? socialPlatforms : adsPlatforms).map((platform) => (
                <div
                  key={platform}
                  onClick={() => setSelectedPlatform(platform)}
                  className={`bg-[#2a2c29] p-4 rounded-xl border cursor-pointer transition-all text-center ${
                    selectedPlatform === platform
                      ? 'border-[#c8f000] bg-[#343633]'
                      : 'border-[#343633] hover:border-[#6f716d]'
                  }`}
                >
                  <span className={`font-medium ${
                    selectedPlatform === platform ? 'text-[#c8f000]' : 'text-white'
                  }`}>{platform}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Step 3: Add-on */}
      {includedCategory && (
        <section className="py-12 bg-[#1f211f]">
          <div className="max-w-[900px] mx-auto px-5 md:px-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 rounded-full bg-[#343633] flex items-center justify-center text-white font-bold text-sm">
                {(includedCategory === 'social' || includedCategory === 'ads') ? '3' : '2'}
              </div>
              <h2 className="text-white font-bold text-xl">{t('checkout.premium.step3_title')}</h2>
            </div>
            <p className="text-[#9a9a96] mb-8 ml-11">
              {t('checkout.premium.step3_desc')}
            </p>

            <div className="space-y-4 ml-11">
              {filteredAddons.map((addon) => (
                <div
                  key={addon.code}
                  onClick={() => toggleAddon(addon.code)}
                  className={`bg-[#2a2c29] p-5 rounded-xl border cursor-pointer transition-all ${
                    selectedAddons.includes(addon.code)
                      ? 'border-[#c8f000]'
                      : 'border-[#343633] hover:border-[#6f716d]'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-6 h-6 rounded border-2 flex-shrink-0 flex items-center justify-center mt-0.5 transition-colors ${
                      selectedAddons.includes(addon.code)
                        ? 'bg-[#c8f000] border-[#c8f000]'
                        : 'border-[#6f716d]'
                    }`}>
                      {selectedAddons.includes(addon.code) && <Check size={14} className="text-[#161716]" />}
                    </div>
                    <div className="flex-grow">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                        <h3 className="text-white font-medium">{t(addon.nameKey)}</h3>
                        <div className="text-right">
                          {addon.priceMonthly && (
                            <span className="text-[#c8f000] font-semibold">CHF {addon.priceMonthly}{t('checkout.per_month')}</span>
                          )}
                          {addon.priceOneShot && (
                            <span className="text-[#c8f000] font-semibold">CHF {addon.priceOneShot} {t('checkout.one_time')}</span>
                          )}
                        </div>
                      </div>
                      <p className="text-[#9a9a96] text-sm">{t(addon.descKey)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upgrade message */}
      {showUpgradeMessage && (
        <section className="py-8 bg-[#161716]">
          <div className="max-w-[900px] mx-auto px-5 md:px-10">
            <div className="bg-[#343633] p-6 rounded-xl border border-[#c8f000]">
              <p className="text-white mb-4">
                {t('checkout.premium.upgrade_message')}
              </p>
              <button
                onClick={() => navigate('/checkout/gold')}
                className="btn-primary"
              >
                {t('checkout.premium.upgrade_cta')}
                <ArrowUpRight className="ml-2" size={16} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Email Input Section */}
      {includedCategory && (
        <section className="py-8 bg-[#161716]">
          <div className="max-w-[900px] mx-auto px-5 md:px-10">
            <div className="bg-[#2a2c29] p-6 rounded-xl border border-[#343633]">
              <label className="block text-white font-medium mb-2">
                {t('checkout.email_label')} <span className="text-[#c8f000]">*</span>
              </label>
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder={t('checkout.email_placeholder')}
                className="w-full px-4 py-3 bg-[#161716] border border-[#343633] rounded-lg text-white placeholder-[#6f716d] focus:outline-none focus:border-[#c8f000] transition-colors"
              />
            </div>
          </div>
        </section>
      )}

      {/* Riepilogo e CTA */}
      <section className="py-12 bg-[#1f211f] border-t border-[#343633] sticky bottom-0">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <p className="text-[#9a9a96] text-sm mb-1">{t('checkout.total_estimated')}</p>
              <div className="flex items-baseline gap-4">
                <span className="text-3xl font-bold text-white">CHF {totals.monthly}</span>
                <span className="text-[#9a9a96]">{t('checkout.per_month')}</span>
              </div>
              {totals.oneShot > 0 && (
                <p className="text-[#c8f000] text-sm mt-1">
                  + CHF {totals.oneShot} {t('checkout.one_time')}
                </p>
              )}
              <p className="text-[#6f716d] text-xs mt-2">{t('checkout.total_note')}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button 
                onClick={handleCheckout}
                className="btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isProcessing || !includedCategory || !customerEmail || ((includedCategory === 'social' || includedCategory === 'ads') && !selectedPlatform)}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={16} />
                    {t('checkout.processing')}
                  </>
                ) : (
                  <>
                    {t('checkout.proceed')}
                    <ArrowRight className="ml-2" size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CheckoutPremium;
